<template>
  <v-row class="fill-height" no-gutters>
    <v-col cols="12">
      <template v-if="isFetched">
        <card-title-row
          is-floating
          :featureTitle="cardSubheader"
          :has-back-button="hasBackButton"
          has-tools-buttons
          @action="backButtonAction"
          class="py-4"
        >
          <template #buttons v-if="!isNew && !treeViewFeature">
            <slot name="buttons">
              <dots-menu
                v-if="!hideDotsItems"
                :items="dotsMenuItems"
                @edit="startEditing('edit')"
                @delete="deleteObjectInit"
              />
            </slot>
          </template>
          <template #toolsButtons v-if="(!isZdmDataScopeLayer && !isNew) || treeViewFeature">
            <slot name="toolsButtons">
              <v-row>
                <base-rounded-button
                  v-if="!hideFeatureWatcher"
                  :color="!isFeatureWatched ? 'rgba(235, 235, 235, 0.5)' : 'primarybackground'"
                  :iconColor="!isFeatureWatched ? '#202124' : '#1A73E8'"
                  icon="mdi-eye-outline"
                  size="x-small"
                  @click="handleWatcher"
                  :tooltip="!isFeatureWatched ? $i18n.t('sidebar.observeObject') : $i18n.t('sidebar.unobserveObject')"
                />
                <base-rounded-button
                  v-if="layer.geometry_type"
                  color="rgba(235, 235, 235, 0.5)"
                  iconColor="#202124"
                  icon="mdi-magnify"
                  size="x-small"
                  @click="zoomToFeature"
                  :tooltip="$i18n.t('sidebar.zoomOnMap')"
                />
                <base-rounded-button
                  size="x-small"
                  @click="openZSIDialog"
                  :tooltip="$i18n.t('dialog.external_systems.tooltip')"
                  inner-text="ZSI"
                  color="rgba(235, 235, 235, 0.5)"
                  iconColor="#202124"
                  v-if="isExternalSystemsEnabled && isDatasourceConnectedToExternalSystem"
                />
                <slot name="toolsCustomButtons" />
                <feature-card-show-buttons
                  v-if="!hideObjectExtras"
                  :permissions="commentAttachmentsPermissions"
                  @showNotes="showExtrasTab('notes')"
                  @showHistory="showExtrasTab('history')"
                  @showAttachments="showExtrasTab('attachments')"
                  @showRelations="showExtrasTab('relations')"
                  :featureProps="properties"
                  :icons-to-show="iconsToShow"
                />
                <v-badge
                  v-if="isVideoModuleVisible"
                  :content="relationsVideoModuleAmount"
                  :value="relationsVideoModuleAmount > 0"
                  :offset-y="15"
                  :offset-x="15"
                  color="error"
                  overlap
                  bordered
                >
                  <base-rounded-button
                    color="rgba(235, 235, 235, 0.5)"
                    icon="mdi-video"
                    icon-color="#202124"
                    size="x-small"
                    :tooltip="$i18n.t('sidebar.showRelations')"
                    @click="showExtrasTab('relations')"
                  />
                </v-badge>
                <feature-card-pdf
                  v-if="!treeViewFeature"
                  :feature-properties="properties"
                  :feature="feature"
                  :layer="layer"
                  :formSchema="formSchema"
                  :file-name="Object.values(cardSubheader).join(': ')"
                />
              </v-row>
            </slot>
          </template>
          <template #toolsButtons v-else-if="isZdmDataScopeLayer && !isNew">
            <slot name="toolsButtons">
              <v-row>
                <feature-card-zdm-buttons
                  :geometry="geometry"
                  :permissions="commentAttachmentsPermissions"
                  @showNotes="showExtrasTab('notes')"
                  @showHistory="showExtrasTab('history')"
                  @showAttachments="showExtrasTab('attachments')"
                  @showRelations="showExtrasTab('relations')"
                  @external_systems="openZSIDialog"
                  :layer-id="layerId"
                  :feature-id="featureId"
                  :dataSource="currentLayerDataSource"
                  :number-attribute-value="numberAttributeValue"
                  :street-attribute-value="streetAttributeValue"
                  :district-attribute-value="districtAttributeValue"
                  :featureProps="properties"
                />
              </v-row>
            </slot>
          </template>
        </card-title-row>
        <sidebar-row v-if="!!layerImageAttributeValue && isImageVisible" class="pt-4">
          <v-hover>
            <template #default="{ hover }">
              <v-col style="width: 23vw; position: relative" class="d-flex pa-0">
                <img :src="layerImageAttributeValue" style="width: 100%" @error="isImageVisible = false" />
                <v-btn
                  v-if="hover"
                  style="position: absolute; top: 3px; right: 3px"
                  class="pa-0"
                  color="rgba(255,255,255,0.5)"
                  height="30"
                  min-width="30"
                  depressed
                  @click="isFullScreenImageVisible = true"
                >
                  <v-icon color="rgba(0,0,0,0.6)">mdi-fullscreen</v-icon>
                </v-btn>
              </v-col>
            </template>
          </v-hover>
        </sidebar-row>
        <sidebar-row
          v-if="currentLayerSourceName === zdmDataSources.cable.datasource && !isNew"
          class="pr-5 pt-3"
          :border-bottom="true"
        >
          <zdm-relation-widget :data-source="zdmDataSources.cable.datasource" :featureId="featureId" class="pb-2" />
        </sidebar-row>
        <v-row no-gutters>
          <v-col cols="12" style="max-width: 100%">
            <slot name="top" v-bind="$props"> </slot>
          </v-col>
        </v-row>
        <v-form v-model="isFormValid">
          <feature-card-data
            :elements="formSchema"
            v-model="values"
            :is-editing-on="isEditingOn && formDataRefreshed"
            :layer-id="layerId"
            arrows-mode="on-with-storage"
            :geometry="geometry"
            @input="onValuesInput"
          />
        </v-form>
        <object-extras
          ref="extras"
          v-if="!isNew && !hideObjectExtras"
          :dataSourceName="currentLayerDataSource"
          :featureId="featureId"
          :layerId="layerId"
          :permissions="commentAttachmentsPermissions"
          :tabs="objectExtrasTabs"
          :geometry="geometry"
        />
      </template>
      <main-loader v-else class="pt-10" />
      <main-dialog
        :is-visible.sync="isDeleteDialogVisible"
        v-if="isDeleteDialogLoaded"
        :title="$i18n.t('dialog.title.deleteObject')"
        :body="deleteBody"
        agree-text="dialog.agreeDelete"
        @agree="deleteObjectHandler"
        :loading="agreeButtonLoading"
      />
      <external-systems-dialog
        :is-visible.sync="isZSIDialogVisible"
        v-if="isZSIDialogLoaded"
        :feature-id="featureId"
        :system-id="datasourceSystemId"
        :datasource="currentLayerDataSource"
      />
    </v-col>
    <image-lightbox :items="[layerImageAttributeValue]" :visible.sync="isFullScreenImageVisible" />
  </v-row>
</template>

<script>
import { get, call, sync } from 'vuex-pathify';
import { cloneDeep, debounce } from 'lodash';

import defaultValues from '@/mixins/defaultValues';
import deleteObject from '@/mixins/deleteObject';
import featureCardHighlight from '@/mixins/featureCardHighlight';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import featureCardZdmButtonsMixin from '@/mixins/featureCardZdmButtonsMixin';

import { prepareDrawnGeometry } from '@/assets/js/mapUtils';
import CardTitleRow from '@/components/CardTitleRow';
import MainLoader from '@/components/MainLoader';
import ObjectExtras from '@/components/ObjectExtras';
import FeatureCardData from '@/components/FeatureCardData';
import DotsMenu from '@/components/DotsMenu';
import FeatureCardZdmButtons from '@/components/FeatureCardZdmButtons';
import FeatureCardShowButtons from '@/components/FeatureCardShowButtons';

import ZdmRelationWidget from '@/components/ZdmRelationWidget';
import SidebarRow from '@/components/SidebarRow';
import FeatureCardPdf from '@/components/FeatureCardPdf';

export default {
  name: 'FeatureCardBasic',
  mixins: [
    defaultValues,
    deleteObject,
    featureCardHighlight,
    prepareAttributesFeatureCardMixin,
    featureCardZdmButtonsMixin,
  ],
  components: {
    ExternalSystemsDialog: () => import('@/components/ExternalSystemsDialog'),
    ObjectExtras,
    CardTitleRow,
    MainLoader,
    FeatureCardData,
    DotsMenu,
    FeatureCardZdmButtons,
    ZdmRelationWidget,
    SidebarRow,
    FeatureCardShowButtons,
    MainDialog: () => import('@/components/MainDialog'),
    FeatureCardPdf,
    ImageLightbox: () => import('@/components/ImageLightbox'),
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    layer: {
      type: Object,
      required: true,
    },
    treeViewFeature: {
      type: Object,
    },
    updateTreeViewAfterEdit: {
      type: Function,
      default: () => {},
    },
    objectExtrasTabs: {
      type: Array,
    },
    customAddNewObjectAction: {
      type: Function,
    },
    customIsFormValid: {
      type: Boolean,
      default: true,
    },
    iconsToShow: {
      type: Array,
      default: () => ['showHistory', 'showNotes', 'showAttachments', 'showRelations'],
    },
    hideObjectExtras: {
      type: Boolean,
      default: false,
    },
    hideFeatureWatcher: {
      type: Boolean,
      default: false,
    },
    hideDotsItems: {
      type: Boolean,
      default: false,
    },
    fetchRelationsOnInit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isZSIDialogVisible: false,
    isZSIDialogLoaded: false,
    isFetched: false,
    formSchema: [],
    isFormValid: true,
    values: {},
    isEditingOn: false,
    geometry: undefined,
    formDataRefreshed: true,
    geometryChanged: false,
    isImageVisible: true,
    isFullScreenImageVisible: false,
    properties: {},
  }),
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    // TODO - move it to props
    commentAttachmentsPermissions: get('users/layersPermissions@:layerId.attachments_and_comments_value'),
    currentLayer: get('layers/currentLayer'),
    currentLayerScope: get('layers/currentLayer@layer_scope'),
    currentLayerId: get('layers/currentLayer@id'),
    currentLayerName: get('layers/currentLayer@name'),
    idPropertyName: get('layers/metadata@:currentLayerDataSource.attributes_schema.id_name'),
    geometryPropertyName: get('layers/metadata@:currentLayerDataSource.attributes_schema.geometry_name'),
    currentLayerAttributesMetadata: get('layers/metadata@:currentLayerDataSource.attributes_schema.attributes'),
    geometryReadOnly() {
      return (
        this.currentLayerAttributesMetadata?.find(attribute => attribute.name === this.geometryPropertyName)
          ?.read_only || false
      );
    },
    currentTab: sync('sidebar/currentTab'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    stickyButtons: sync('sidebar/stickyButtons'),
    isEditingButtonActive: sync('edit/isEditingButtonActive'),
    isAddingButtonActive: sync('edit/isAddingButtonActive'),
    isCopyButtonActive: sync('edit/isCopyButtonActive'),
    isAssignParcelButtonActive: sync('edit/isAssignParcelButtonActive'),
    copiedObject: get('edit/copiedObject'),
    tableSelectionType: get('edit/tableSelectionType'),
    watermetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    dmaLayerId: get('admin/modulesMapping@billing.dma_layer_id'),
    permissions: get('users/layersPermissions'),
    attrSchemaMetadata: get('layers/metadata'),
    currentLayerSourceName: get('layers/currentLayer@data_source_name'),
    isDatasourceConnectedToExternalSystem: get('externalSystems/connectedDataSources@:currentLayerDataSource'),
    isExternalSystemsEnabled: get('admin/settingsValues@external_systems_module_enabled'),
    zdmDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    isFeatureWatched: sync('watcher/isFeatureWatched'),
    currentLayerRelations: get('relations/relations@:currentLayerSourceName'),
    videoModuleDataSourceName: get('admin/modulesMapping@video.selected_datasource_name'),
    isVideoModuleSuperAdminEnabled: get('admin/additionalModules@VIDEO_MODULE.enabled'),
    isVideoModuleAdminEnabled: get('admin/settingsValues@video_module_enabled'),
    isVideoModulePermission: get('users/toolsPermissions@VIDEO_MODULE.main_value'),
    isVideoModuleVisible() {
      return (
        this.isVideoModuleSuperAdminEnabled &&
        this.isVideoModuleAdminEnabled &&
        this.isVideoModulePermission &&
        this.currentLayerSourceName !== this.videoModuleDataSourceName &&
        this.isGeometricalLayer
      );
    },
    relationsVideoModuleAmount() {
      return this.currentLayerRelations
        ? this.currentLayerRelations[this.featureId]?.filter(
            relation => relation.data_source_name === this.videoModuleDataSourceName
          )?.length
        : 0;
    },
    isZdmDataScopeLayer() {
      return this.layer.layer_scope === 'zdm_data';
    },
    datasourceSystemId() {
      return this.isDatasourceConnectedToExternalSystem?.id;
    },
    isNewCard: sync('map/isNewCard'),
    currentRouteName() {
      return this.$route.name;
    },
    customRoutesLayersIds() {
      return {
        legalizationInfo: this.watermetersLayerId,
        waterDissolutionWatermetersInfo: this.watermetersLayerId,
        waterDissolutionDmaInfo: this.dmaLayerId,
        billingAnomalyDetectionInfo: this.watermetersLayerId,
      };
    },
    customRoutesParentsNames() {
      return {
        legalizationInfo: 'legalization',
        waterDissolutionWatermetersInfo: 'waterDissolutionWatermeters',
        waterDissolutionDmaInfo: 'waterDissolutionDma',
        billingAnomalyDetectionInfo: 'billingAnomalyDetection',
      };
    },
    hasBackButton() {
      return this.customRoutesLayersIds[this.currentRouteName] ? true : false;
    },
    routeParams() {
      return this.$route.params;
    },
    featureId() {
      return this.routeParams.fid;
    },
    layerId() {
      return this.routeParams?.lid || this.layer?.id;
    },
    routeQuery() {
      return this.$route.query;
    },
    treeViewFeatureId() {
      return this.routeQuery.fid;
    },
    treeViewLayerId() {
      return this.routeQuery.lid;
    },
    computedTreeViewLidFid() {
      return {
        fid: this.treeViewFeatureId,
        lid: this.treeViewLayerId,
      };
    },
    treeViewLayerFormSchema: get('layers/layers@:treeViewLayerId@form_schema.elements'),
    treeViewLayerDataSource: get('layers/layers@:treeViewLayerId@data_source_name'),
    treeViewLayerScope: get('layers/layers@:treeViewLayerId@layer_scope'),
    treeViewAttributesMetadata: get('layers/metadata@:treeViewLayerDataSource.attributes_schema.attributes'),
    treeViewIdPropertyName: get('layers/metadata@:treeViewLayerDataSource.attributes_schema.id_name'),
    treeViewGeometryPropertyName: get('layers/metadata@:treeViewLayerDataSource.attributes_schema.geometry_name'),
    hasTreeViewFeature() {
      return this.treeViewFeatureId && this.treeViewLayerId && this.treeViewFeature;
    },
    editType() {
      return this.$route.query.editType;
    },
    cardSubheader() {
      if (this.title) {
        return { title: this.title };
      }
      if (this.isNew) {
        return {
          title: this.$i18n.t('sidebar.newObject'),
          subtitle: this.currentLayerName,
        };
      }
      return {
        title: this.values[this.attrSchemaMetadata[this.currentLayerSourceName].attributes_schema.desc_attribute_name],
        subtitle: this.currentLayerName,
      };
    },
    computedLidFid() {
      return {
        fid: this.featureId,
        lid: this.layerId,
      };
    },
    isNew() {
      if (this.hasTreeViewFeature) {
        return this.treeViewFeatureId === 'new';
      }
      return this.$route.params.fid === 'new';
    },
    hasEditPermissions() {
      return this.permissions && this.permissions[this.layerId]
        ? this.permissions[this.layerId].main_value === 2
        : false;
    },
    deleteBody() {
      return this.$i18n.t('dialog.body.deleteObject', { id: this.featureId });
    },
    dotsMenuItems() {
      return [
        {
          name: 'edit',
          disabled: !this.hasEditPermissions || this.isEditingOn || !this.formDataRefreshed,
        },
        {
          name: 'delete',
          disabled: !this.hasEditPermissions || !this.formDataRefreshed,
        },
      ];
    },
    isGeometricalLayer() {
      return Boolean(this.currentLayer.geometry_type);
    },
    isTopologicalLayer() {
      const scope = this.hasTreeViewFeature ? this.treeViewLayerScope : this.currentLayerScope;
      return scope === 'water' || scope === 'sewer';
    },
    numberAttributeName() {
      return this.$_objectToArray(this.zdmDataSources).find(d => d.datasource === this.layer.data_source_name)
        ?.display_attribute_name;
    },
    streetAttributeName() {
      return this.$_objectToArray(this.zdmDataSources).find(d => d.datasource === this.layer.data_source_name)
        ?.street_attribute_name;
    },
    districtAttributeName() {
      return this.$_objectToArray(this.zdmDataSources).find(d => d.datasource === this.layer.data_source_name)
        ?.district_attribute_name;
    },
    numberAttributeValue() {
      return this.values[this.numberAttributeName];
    },
    streetAttributeValue() {
      return this.values[this.streetAttributeName];
    },
    districtAttributeValue() {
      return this.values[this.districtAttributeName];
    },
    computedIsValid() {
      return this.isFormValid && this.customIsFormValid;
    },
    newTopoIndexes: sync('edit/newTopoIndexes'),
    deletedTopoIndexes: sync('edit/deletedTopoIndexes'),
    layerImageAttribute() {
      return this.currentLayer?.image_attribute;
    },
    token: get('authentication/token'),
    layerImageAttributeValue() {
      const imgUrl = this.properties[this.layerImageAttribute];
      return imgUrl ? `${imgUrl}${imgUrl.startsWith(process.env.VUE_APP_API_URL) ? `?token=${this.token}` : ''}` : '';
    },
  },
  watch: {
    computedLidFid: {
      immediate: true,
      deep: true,
      handler(nV, oV) {
        if (oV?.fid === nV?.fid && oV?.lid === nV?.lid) {
          return;
        }
        this.$nextTick(() => {
          this.onLidFidChanged(nV, oV);
        });
      },
    },
    computedTreeViewLidFid: {
      immediate: true,
      deep: true,
      handler(nV, oV) {
        if (oV?.fid === nV?.fid && oV?.lid === nV?.lid) {
          return;
        }
        this.$nextTick(() => {
          this.onTreeViewLidFidChanged(nV, oV);
        });
      },
    },
    computedIsValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
    featureId: {
      immediate: true,
      async handler(nV) {
        this.isFeatureWatched = false;
        if (!this.isNew && !this.hideFeatureWatcher) {
          await this.checkWatching({ datasource: this.currentLayerSourceName, object_id: nV });
        }
      },
    },
    isEditingOn: {
      immediate: true,
      handler(nV) {
        this.$emit('update:isEditingOn', nV);
      },
    },
    layerImageAttributeValue(nV) {
      if (nV) {
        this.isImageVisible = true;
      }
    },
  },
  methods: {
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getLayerFeature: call('layers/getLayerFeature'),
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    addTopoFeatureAction: call('layers/addTopoDatasourceFeature'),
    editTopoFeatureAction: call('layers/editTopoDatasourceFeature'),
    checkDatasourceConnection: call('externalSystems/checkDatasourceConnection'),
    addWatching: call('watcher/addWatching'),
    stopWatching: call('watcher/stopWatching'),
    checkWatching: call('watcher/checkWatching'),
    getRelations: call('relations/getRelations'),
    handleWatcher() {
      const payload = { datasource: this.currentLayerSourceName, object_id: this.featureId };
      this.isFeatureWatched ? this.stopWatched(payload) : this.addToWatched(payload);
    },
    async addToWatched(payload) {
      await this.addWatching(payload);
    },
    async stopWatched(payload) {
      await this.stopWatching(payload);
    },
    refreshFormData() {
      this.updateAfterEdit(true);
    },
    backButtonAction() {
      this.$router.push({ name: this.customRoutesParentsNames[this.currentRouteName] });
    },
    zoomToFeature() {
      const { crs, geometry } = this.feature;
      this.$root.$emit('createGeomAndFitView', { ...geometry, crs });
    },
    pushToParentRoute() {
      this.$router.push({
        name: this.$route.meta.parent,
        params: {
          ...this.$route.params,
          lid: this.layerId,
        },
      });
      this.currentTab = 0;
    },
    setProperties(feature) {
      this.$emit('update:feature', feature);
      this.properties = this.getFlattenProperties(
        feature,
        this.hasTreeViewFeature ? this.treeViewIdPropertyName : this.idPropertyName
      );
    },
    async getFeature(lid = this.layerId, fid = this.featureId) {
      try {
        this.feature = this.hasTreeViewFeature
          ? this.treeViewFeature
          : await this.getLayerFeature({ layer_id: lid, feature_id: fid });
        this.setProperties(this.feature);
        this.geometry = { ...this.feature.geometry, crs: this.feature.crs };
        if (this.isEditingButtonActive) {
          this.startEditing('edit');
        }
      } catch (e) {
        this.pushToParentRoute();
        console.log(e);
      }
    },
    onNewFid() {
      if (this.isGeometricalLayer && !this.newFeatureGeometry && !this.hasTreeViewFeature) {
        this.pushToParentRoute();
      } else {
        this.feature = {
          ...(this.isGeometricalLayer && !this.hasTreeViewFeature ? { geometry: this.newFeatureGeometry } : {}),
        };
        this.geometry = this.isGeometricalLayer && !this.hasTreeViewFeature ? this.newFeatureGeometry : undefined;

        if (this.hasTreeViewFeature) {
          this.properties =
            this.copiedObject || this.getFlattenProperties(this.treeViewFeature, this.treeViewIdPropertyName) || {};
          this.properties[this.treeViewGeometryPropertyName] = this.treeViewFeature.geometry;
          delete this.properties[this.treeViewIdPropertyName];
        } else {
          this.properties = this.copiedObject || {};
        }
        this.startEditing(this.copiedObject ? 'copy' : 'add');
      }
    },
    async updateAfterEdit(onlyRefresh = false) {
      try {
        if (this.hasTreeViewFeature) {
          await this.updateTreeViewAfterEdit(true);
        }
        this[onlyRefresh ? 'formDataRefreshed' : 'isFetched'] = false;
        await this.getFeature();
        this.values = this.getValues(this.formSchema, this.properties);
        this[onlyRefresh ? 'formDataRefreshed' : 'isFetched'] = true;
      } catch (e) {
        console.log(e);
      }
    },
    async onTreeViewLidFidChanged() {
      if (this.isEditingOn) {
        this.stopEditing();
      }
      if (this.isNew) {
        this.onNewFid();
      } else {
        await this.getFeature();
      }
      this.formSchema = this.hasTreeViewFeature
        ? this.getForm(this.treeViewLayerFormSchema, this.treeViewAttributesMetadata)
        : this.getForm(this.currentLayerFormSchema, this.attributesMetadata);
      this.values = this.getValues(this.formSchema, this.properties);
      if (!this.isNew && this.isNewCard) {
        this.isNewCard = false;
        if (this.isGeometricalLayer) {
          this.zoomToFeature();
        }
      }
    },
    async onLidFidChanged(nV = this.computedLidFid, oV) {
      if (!this.mapPanelDataFetched) {
        return;
      }
      try {
        if (this.isEditingOn) {
          this.stopEditing();
        }
        this.isFetched = false;
        this.clearHighlitedFeatureHandler(nV.lid, oV?.lid);
        if (this.isNew) {
          this.onNewFid();
        } else {
          if (this.isVideoModuleVisible || this.fetchRelationsOnInit) {
            const payload = {
              data_source_name: this.currentLayerSourceName,
              feature_id: nV.fid,
            };
            await this.getRelations(payload);
          }
          await this.getFeature(nV.lid, nV.fid);
          // this.feature.id instead of nV.fid as this.$route.params.fid may be String instead of Number (usually happens on app init)
          this.highlightFeature(nV.lid, this.feature.id);
        }
        this.formSchema = this.hasTreeViewFeature
          ? this.getForm(this.treeViewLayerFormSchema, this.treeViewAttributesMetadata)
          : this.getForm(this.currentLayerFormSchema, this.attributesMetadata);
        this.values = this.getValues(this.formSchema, this.properties);
        this.valuesCopy = cloneDeep(this.values);
        this.isFetched = true;
        if (!this.isNew && this.isNewCard) {
          this.isNewCard = false;
          if (this.isGeometricalLayer) {
            this.zoomToFeature();
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    startEditing(type) {
      this.geometryChanged = false;
      if (!this.isNew) {
        this.featureCopy = cloneDeep(this.feature);
        this.valuesCopy = cloneDeep(this.values);
        this.geometryCopy = cloneDeep(this.geometry);
      }
      const dict = {
        add: 'isAddingButtonActive',
        copy: 'isCopyButtonActive',
        edit: 'isEditingButtonActive',
      };
      this[dict[type]] = true;
      this.isEditingOn = true;
      this.isStickyButtonsVisible = true;
      this.stickyButtons = [
        {
          action: this.isNew ? 'featureAdding' : 'featureEditing',
          translationPath: 'dialog.save',
        },
        {
          action: 'cancelEditing',
          translationPath: 'dialog.disagree',
          cancel: true,
        },
      ];
      this.$root.$emit('selection-action');
      if (!this.isGeometricalLayer || this.geometryReadOnly) {
        return;
      }
      this.$root.$emit('modifySidebarGeometry', {
        geometryValue: this.geometry,
        modifyendCallback: this.onModifyend,
        layerId: this.hasTreeViewFeature ? this.treeViewLayerId : this.layerId,
        isInitSnapping: true,
      });
    },
    cancelEditing() {
      this.stopEditing();
      if (this.hasTreeViewFeature && this.isNew) {
        this.$emit('cancelTreeViewElementAdding');
        this.isAddingButtonActive = false;
        return;
      } else if (this.isNew) {
        this.pushToParentRoute();
        this.isAddingButtonActive = false;
        return;
      }
      this.feature = cloneDeep(this.featureCopy);
      this.values = cloneDeep(this.valuesCopy);
      this.geometry = cloneDeep(this.geometryCopy);
    },
    stopEditing(selection = true) {
      this.clearButtons();
      // this.isCopyButtonActive = false;
      if (selection) {
        this.$root.$emit('selection-action', this.tableSelectionType);
      }
      this.$root.$emit('deleteSidebarGeometry');
    },
    clearButtons() {
      this.isEditingOn = false;
      this.isStickyButtonsVisible = false;
      this.isStickyButtonsLoading = false;
      this.stickyButtons = [];
      this.isEditingButtonActive = false;
      this.isAddingButtonActive = false;
      this.isAssignParcelButtonActive = false;
    },
    setSidebarGeometry(feature) {
      if (!feature) {
        this.geometry = null;
      } else {
        this.geometry = prepareDrawnGeometry(feature);
      }
    },
    onModifyend(e) {
      this.geometryChanged = true;
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    handleFeatureAdding() {
      if (this.isTopologicalLayer) {
        this.addTopoFeature();
      } else {
        this.addNonTopoFeature();
      }
    },
    handleFeatureEditing() {
      if (this.isTopologicalLayer) {
        this.editTopoFeature();
      } else {
        this.editNonTopoFeature();
      }
    },
    pushMessage(message) {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message,
      });
    },
    async editingAddingHof(fn, payload, message) {
      try {
        this.isStickyButtonsLoading = true;
        const r = await fn(payload);
        this.stopEditing();
        this.pushMessage(message);
        this.isStickyButtonsLoading = false;
        return r;
      } catch (error) {
        this.cancelEditing();
        console.log(error);
        throw new Error(error);
      }
    },
    async pushRouter(fid) {
      if (this.hasTreeViewFeature) {
        await this.$router.push({
          name: this.$route.name,
          params: this.$route.params,
          query: { ...this.$route.query, fid },
        });
        await this.updateTreeViewAfterEdit(false, true);
      } else {
        await this.$router.push({ name: this.$route.name, params: { ...this.$route.params, fid } });
      }
    },
    async addingHof(fn, payload) {
      const r = await this.editingAddingHof(fn, payload, this.$i18n.t('success.successAddedObject'));
      if (this.isCopyButtonActive) {
        this.$root.$emit('turnOnCopying');
      }
      return r;
    },
    async addNonTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.currentLayerDataSource;
      const action = this.customAddNewObjectAction || this.addNonTopoFeatureAction;
      const r = await this.addingHof(action, {
        datasource,
        body: this.getPayload(this.hasTreeViewFeature),
        ...(action === this.addNonTopoFeatureAction && {
          params: {
            layer_id: this.layer.id,
          },
        }),
      });
      this.$store.set('attributesSchema/DELETE_CACHED_RELATIONS_BY_DATASOURCE!', datasource);
      this.refreshTableAndMap();
      await this.pushRouter(r.data.data);
    },
    async addTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.currentLayerDataSource;
      const properties = { ...this.values };
      delete properties.id;
      const action = this.customAddNewObjectAction || this.addTopoFeatureAction;
      const r = await this.addingHof(action, {
        datasource,
        body: {
          properties,
          geometry: this.geometry,
        },
        params: { layer_id: this.layerId },
      });
      await this.pushRouter(r.data.data.inserted);
      this.refreshLayersAndTable(r.data.data.layers_id);
    },
    async editingHof(fn, payload) {
      const r = await this.editingAddingHof(fn, payload, this.$i18n.t('success.successEditObjects'));
      this.refreshTableAndMap();
      this.updateAfterEdit();
      return r;
    },
    async editNonTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.currentLayerDataSource;
      const id = this.hasTreeViewFeature ? this.treeViewFeatureId : this.featureId;
      await this.editingHof(this.editNonTopoFeatureAction, {
        datasource,
        id,
        body: this.getPayload(),
        params: { layer_id: this.layerId },
      });
      this.geometryChanged = false;
    },
    async editTopoFeature() {
      const datasource = this.hasTreeViewFeature ? this.treeViewLayerDataSource : this.currentLayerDataSource;
      const properties = { ...this.values };
      delete properties.id;
      const r = await this.editingHof(this.editTopoFeatureAction, {
        datasource,
        id: this.featureId,
        body: {
          properties,
          ...(this.geometryChanged ? { geometry: this.geometry } : {}),
          created: this.newTopoIndexes,
          deleted: this.deletedTopoIndexes,
        },
        params: { layer_id: this.layerId },
      });
      this.refreshLayersAndTable(r.data.data);
      this.geometryChanged = false;
    },
    deleteObjectInit() {
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    openZSIDialog() {
      this.isZSIDialogLoaded = true;
      this.isZSIDialogVisible = true;
    },
    async deleteObjectHof(fn) {
      try {
        this.agreeButtonLoading = true;
        this.stopEditing();
        const { currentLayerDataSource, featureId } = this;
        const r = this.isTopologicalLayer
          ? await fn({
              datasource: currentLayerDataSource,
              payload: { to_delete: [featureId] },
              params: { layer_id: this.layer.id },
            })
          : await fn({ datasource: currentLayerDataSource, id: featureId, params: { layer_id: this.layer.id } });
        this.refreshTableAndMap();
        this.pushToParentRoute();
        this.agreeButtonLoading = false;
        this.isDeleteDialogVisible = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successDeleteObject'),
        });
        return r;
      } catch (error) {
        this.agreeButtonLoading = false;
        this.isDeleteDialogVisible = false;
      }
    },
    getPayload(addTreeViewElement = false) {
      const geometryPropertyName = this.hasTreeViewFeature
        ? this.treeViewGeometryPropertyName
        : this.geometryPropertyName;
      return {
        ...this.values,
        ...(this.isGeometricalLayer && !this.geometryReadOnly && (this.geometryChanged || this.isNew)
          ? {
              [geometryPropertyName]: addTreeViewElement
                ? { ...this.treeViewFeature.geometry, crs: this.treeViewFeature.crs }
                : this.geometry,
            }
          : {}),
      };
    },
    refreshTableAndMap() {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshMvtSource', this.layerId);
      this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.layerId] });
    },
    toggleListeners(isOn) {
      const method = isOn ? '$on' : '$off';
      this.$root[method]('featureAdding', this.handleFeatureAdding);
      this.$root[method]('featureEditing', this.handleFeatureEditing);
      this.$root[method]('cancelEditing', this.cancelEditing);
      this.$root[method]('turnOffEditing', this.stopEditing);
      this.$root[method]('turnOnEditing', () => this.startEditing('edit'));
      this.$root[method]('tableDataFetched', this.onLidFidChanged);
      this.$root[method]('refreshFormData', this.refreshFormData);
    },
    onValuesInput(values) {
      this.values = values;
    },
    async init() {
      if (this.isExternalSystemsEnabled) await this.checkDatasourceConnection(this.currentLayerDataSource);
    },
  },
  created() {
    this.onLidFidChanged = debounce(this.onLidFidChanged, 100);
  },
  mounted() {
    this.toggleListeners(true);
    this.init();
  },
  beforeDestroy() {
    this.toggleListeners(false);
    this.clearHighlitedFeature(this.layerId);
    this.clearButtons();
  },
};
</script>

<style lang="scss" scoped></style>
